import { useEffect, useState } from "react";
import { sitesApi } from "../../services/sitesApi";

/* Conditions at a site (#273).
 *
 * The panel is deliberately quiet about its own failures. Weather is the
 * first thing a beekeeper checks before driving out, but it is decoration
 * on every screen that carries it -- so a missing forecast reports itself
 * in a line of text and never as an error state that competes with the
 * work the page is actually for.
 *
 * The one case that gets a real prompt is a site with no coordinates,
 * because that is the only one the person reading can fix. */

const C_TO_F = (c) => (c * 9) / 5 + 32;
const KPH_TO_MPH = (k) => k * 0.621371;

function describeForaging(score) {
  // Says what the number MEANS for the colony, because "68" alone tells a
  // beekeeper nothing. These read as observations, not instructions --
  // whether to open a hive depends on things this panel cannot see.
  if (score === null || score === undefined) return null;
  if (score >= 85) return "Good flying weather";
  if (score >= 60) return "Some foraging";
  if (score >= 30) return "Little flying";
  return "Bees staying in";
}

function hoursOld(observedAt) {
  if (!observedAt) return null;
  const ms = Date.now() - new Date(observedAt).getTime();
  if (Number.isNaN(ms)) return null;
  return ms / 3600000;
}

export function WeatherPanel({ siteId, siteName, latitude, longitude }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    sitesApi
      .weather(siteId)
      .then((data) => {
        if (!cancelled) setState({ loading: false, ...data });
      })
      .catch(() => {
        // A real HTTP failure. Still not an error banner -- the same
        // reasoning as every other branch here.
        if (!cancelled) setState({ loading: false, status: "unavailable" });
      });
    return () => {
      cancelled = true;
    };
    // Also keyed on the coordinates: adding a location to a site that had
    // none must re-ask, or the panel keeps reporting "add a location"
    // over a site that now has one. Reported after testing -- it only
    // corrected itself on a trip to the dashboard and back.
  }, [siteId, latitude, longitude]);

  if (state.loading) {
    return <p style={{ color: "var(--color-text-muted)", margin: 0 }}>Checking conditions…</p>;
  }

  // No key on this server. An operator problem the reader cannot act on,
  // so say nothing at all rather than nag them about it.
  if (state.status === "unconfigured") return null;

  if (state.status === "no_location") {
    return (
      <p style={{ color: "var(--color-text-muted)", margin: 0, fontSize: "var(--font-size-sm)" }}>
        Add a location to {siteName || "this site"} to see conditions here.
      </p>
    );
  }

  const w = state.weather;
  if (!w || w.temperature_c === null || w.temperature_c === undefined) {
    return (
      <p style={{ color: "var(--color-text-muted)", margin: 0, fontSize: "var(--font-size-sm)" }}>
        Conditions aren&apos;t available right now.
      </p>
    );
  }

  const age = hoursOld(w.observed_at);
  const stale = state.status === "unavailable" || (age !== null && age >= 2);
  const foraging = describeForaging(w.foraging_score);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-2)", flexWrap: "wrap" }}>
        <strong style={{ fontSize: "1.5rem" }}>{Math.round(C_TO_F(w.temperature_c))}&deg;F</strong>
        {w.condition && <span style={{ color: "var(--color-text-muted)" }}>{w.condition}</span>}
      </div>

      <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        {w.wind_kph !== null && w.wind_kph !== undefined && (
          <span>Wind {Math.round(KPH_TO_MPH(w.wind_kph))} mph</span>
        )}
        {w.humidity_pct !== null && w.humidity_pct !== undefined && (
          <span> &middot; {Math.round(w.humidity_pct)}% humidity</span>
        )}
      </div>

      {foraging && (
        <div style={{ fontSize: "var(--font-size-sm)" }}>
          {foraging}
          {w.foraging_score !== null && w.foraging_score !== undefined && (
            <span style={{ color: "var(--color-text-muted)" }}> ({w.foraging_score}/100)</span>
          )}
        </div>
      )}

      {/* Age is only worth saying when it changes how much to trust the
          number. A reading from ten minutes ago needs no caveat. */}
      {stale && (
        <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          {age !== null && age >= 1
            ? `Last reading ${Math.round(age)} hour${Math.round(age) === 1 ? "" : "s"} ago`
            : "Last known reading"}
          {state.status === "unavailable" && " — couldn't reach the weather service"}
        </div>
      )}
    </div>
  );
}
